/* eslint-disable max-lines, max-lines-per-function */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import { Menu } from 'primereact/menu';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './ActivityReport.scss';

import { useAuth } from '../../../Hooks/useAuth';
import {
  getActivityReportHolidayListRequest,
  getActivityReportUrl,
} from '../../../Api/request/activityReport';
import moreIcon from '../../../Assets/images/svg/dar_more.svg';

const UTILIZATION_ACTIVITY_REPORT_TITLE = 'Utilization Activity Report';
const UTILIZATION_EMPLOYEE_REPORT_ROUTE = '/mis/employee-utilization-report';
const PROJECT_DETAILS_ROUTE = '/delivery/project-details';
const ACTIVITY_HEADING_PREFIX = `${UTILIZATION_ACTIVITY_REPORT_TITLE} - `;
const ACTIVITY_HEADING_LINK_CLASS = 'activity-report-heading-project-link';

const buildProjectDetailsUrl = (projectId) => (
  `${PROJECT_DETAILS_ROUTE}?projectId=${encodeURIComponent(String(projectId))}`
);

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const flattenMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.flatMap((menuItem) => (Array.isArray(menuItem?.items) ? menuItem.items : []))
    : []
);

const getFilterMenuItem = (tableConfiguration = [], field) => {
  const column = Array.isArray(tableConfiguration)
    ? tableConfiguration.find((item) => item?.field === field)
    : null;

  if (!column) return null;

  return flattenMenuItems(column.menuItems).find(
    (item) => item?.type === 'filter' && item?.field === field,
  ) || null;
};

const getSelectedOptionLabel = (
  selectedValue,
  { allowIdentifierFallback = true } = {},
) => {
  if (!selectedValue) return '';
  if (typeof selectedValue === 'string') return selectedValue.trim();
  if (typeof selectedValue === 'number') return allowIdentifierFallback ? `${selectedValue}` : '';

  if (isNonEmptyString(selectedValue.name)) return selectedValue.name.trim();
  if (isNonEmptyString(selectedValue.label)) return selectedValue.label.trim();
  if (!allowIdentifierFallback) return '';
  if (selectedValue.value !== undefined && selectedValue.value !== null) {
    return `${selectedValue.value}`.trim();
  }
  if (selectedValue.id !== undefined && selectedValue.id !== null) {
    return `${selectedValue.id}`.trim();
  }

  return '';
};

const getReadableSelectedLabel = (selectedValue = null) => {
  const selectedLabel = getSelectedOptionLabel(selectedValue, {
    allowIdentifierFallback: false,
  });

  if (!selectedLabel) {
    return '';
  }

  if (!selectedValue || typeof selectedValue !== 'object') {
    return /^\d+$/.test(selectedLabel) ? '' : selectedLabel;
  }

  const rawIdentifiers = [
    selectedValue.id,
    selectedValue.value,
  ]
    .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
    .map((value) => `${value}`.trim());

  return rawIdentifiers.includes(selectedLabel.trim()) ? '' : selectedLabel;
};

const cloneSelectedValues = (selectedValues = []) => (
  Array.isArray(selectedValues)
    ? selectedValues
      .filter(Boolean)
      .map((selectedValue) => (
        typeof selectedValue === 'object'
          ? { ...selectedValue }
          : {
            id: selectedValue,
            value: selectedValue,
            label: `${selectedValue}`,
            name: `${selectedValue}`,
          }
      ))
    : []
);

const cloneSelectedValue = (selectedValue = null) => {
  if (!selectedValue) return null;
  if (typeof selectedValue === 'object') return { ...selectedValue };

  return {
    id: selectedValue,
    value: selectedValue,
    label: `${selectedValue}`,
    name: `${selectedValue}`,
  };
};

const getOptionLabelFromChoices = (selectedValue, options = []) => {
  if (!selectedValue || !Array.isArray(options) || options.length === 0) {
    return '';
  }

  const selectedOptionKeys = new Set(
    [
      selectedValue?.id,
      selectedValue?.value,
      selectedValue,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim()),
  );

  const matchedOption = options.find((option) => (
    [
      option?.id,
      option?.value,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim())
      .some((value) => selectedOptionKeys.has(value))
  ));

  return getSelectedOptionLabel(matchedOption, { allowIdentifierFallback: false });
};

const getProjectNameFromFilterItem = (filterItem = null) => {
  if (!filterItem || typeof filterItem !== 'object') {
    return '';
  }

  return getReadableSelectedLabel(filterItem.selectedValue)
    || getOptionLabelFromChoices(filterItem.selectedValue, filterItem.options);
};

const parseRequestPayload = (response = {}) => {
  const requestData = response?.config?.data;

  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return typeof requestData === 'object' ? requestData : {};
};

const ACTIVITY_FILTER_FIELDS = [
  'projectVertical',
  'projectType',
  'projectStatus',
  'projectName',
  'activity',
  'total',
  'dateRange',
];

const hasFilterState = (filterItem = null) => {
  if (!filterItem || typeof filterItem !== 'object') {
    return false;
  }

  return FILTER_STATE_KEYS.some((key) => {
    const value = filterItem[key];

    if (Array.isArray(value)) {
      return value.some((item) => item !== null && item !== undefined && `${item}`.trim() !== '');
    }

    return value !== null && value !== undefined && `${value}`.trim() !== '';
  });
};

const getRequestFilters = (requestPayload = {}) => {
  const payload = requestPayload?.payload || {};

  return [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    payload?.filter,
    payload?.filters,
    payload?.filterCriteria,
  ].find(Array.isArray) || [];
};

const buildRequestFilterStateMap = (requestPayload = {}) => {
  const payload = requestPayload?.payload || {};
  const filterStateMap = new Map();
  const addFilterState = (field, filterState) => {
    if (!field || !filterState || typeof filterState !== 'object') {
      return;
    }

    const normalizedFilterState = {
      ...filterState,
      field: filterState.field || field,
      type: filterState.type || 'filter',
    };

    if (hasFilterState(normalizedFilterState)) {
      filterStateMap.set(field, normalizedFilterState);
    }
  };

  getRequestFilters(requestPayload).forEach((filterItem) => {
    addFilterState(filterItem?.field, filterItem);
  });

  ACTIVITY_FILTER_FIELDS.forEach((field) => {
    addFilterState(field, requestPayload?.[field]);
    addFilterState(field, payload?.[field]);
  });

  return filterStateMap;
};

const cloneFilterStateMap = (filterStateMap = new Map()) => (
  new Map(Array.from(filterStateMap.entries()).map(([field, filterState]) => [
    field,
    { ...filterState },
  ]))
);

const getProjectNameFromRequestPayload = (response = {}) => {
  const requestPayload = parseRequestPayload(response);
  const payload = requestPayload?.payload;
  const candidateContainers = [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    requestPayload?.additionalPayloadData,
    payload?.filter,
    payload?.filters,
    payload?.filterCriteria,
    payload?.additionalPayloadData,
    payload,
    requestPayload,
  ].filter(Boolean);

  const projectNameCandidateValue = candidateContainers
    .map((container) => container?.projectName)
    .find(Boolean);
  const projectNameCandidate = Array.isArray(projectNameCandidateValue)
    ? projectNameCandidateValue[0]
    : projectNameCandidateValue;

  if (!projectNameCandidate) {
    return '';
  }

  if (projectNameCandidate?.selectedValue) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValue)
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValue,
        projectNameCandidate.options,
      );
  }

  if (Array.isArray(projectNameCandidate?.selectedValues)) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValues[0])
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValues[0],
        projectNameCandidate.options,
      );
  }

  return getReadableSelectedLabel(projectNameCandidate);
};

const getReportTitleFromResponse = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
  const payload = responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
  const reportTitle = `${payload.reportTitle || payload.title || ''}`.trim();

  return reportTitle.startsWith('Activity Report - ')
    || reportTitle.startsWith(`${UTILIZATION_ACTIVITY_REPORT_TITLE} - `)
    ? reportTitle
    : '';
};

const getResponsePayload = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};

  return responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
};

const areEqualByValue = (leftValue, rightValue) => (
  JSON.stringify(leftValue) === JSON.stringify(rightValue)
);

const getReportTitleFromConfiguration = (
  response,
  tableConfiguration = [],
  defaultTitle = UTILIZATION_ACTIVITY_REPORT_TITLE,
) => {
  const selectedProjectFilter = getFilterMenuItem(tableConfiguration, 'projectName');
  const responseReportTitle = getReportTitleFromResponse(response);
  const projectName = getProjectNameFromReportTitle(responseReportTitle)
    || getProjectNameFromRequestPayload(response)
    || getProjectNameFromFilterItem(selectedProjectFilter);

  return projectName ? `${UTILIZATION_ACTIVITY_REPORT_TITLE} - ${projectName}` : defaultTitle;
};

const getAppliedFilterPayload = (tableConfiguration = []) => {
  const projectVertical = getFilterMenuItem(tableConfiguration, 'projectVertical');
  const projectType = getFilterMenuItem(tableConfiguration, 'projectType');
  const projectStatus = getFilterMenuItem(tableConfiguration, 'projectStatus');
  const projectName = getFilterMenuItem(tableConfiguration, 'projectName');
  const activity = getFilterMenuItem(tableConfiguration, 'activity');
  const total = getFilterMenuItem(tableConfiguration, 'total');
  const dateRange = getFilterMenuItem(tableConfiguration, 'dateRange');

  return {
    projectVertical: cloneSelectedValues(projectVertical?.selectedValues),
    projectType: cloneSelectedValues(projectType?.selectedValues),
    projectStatus: cloneSelectedValues(projectStatus?.selectedValues),
    projectName: cloneSelectedValue(projectName?.selectedValue),
    activity: cloneSelectedValues(activity?.selectedValues),
    total: {
      range: Array.isArray(total?.range) ? [...total.range] : [],
    },
    dateRange: {
      range: Array.isArray(dateRange?.range) ? [...dateRange.range] : [],
    },
  };
};

const getProjectNameFromAppliedFilters = (filterPayload = {}) => (
  getReadableSelectedLabel(filterPayload?.projectName)
);

const sanitizeExportFileSegment = (value, fallback = 'Report') => {
  const normalizedValue = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalizedValue || fallback;
};

const getMonthLabelFromDateRange = (range = []) => {
  const [startDate, endDate] = Array.isArray(range) ? range : [];
  const reportDate = dayjs(startDate || endDate);

  return reportDate.isValid() ? reportDate.format('MMM-YYYY') : dayjs().format('MMM-YYYY');
};

const getExportMonthLabel = (exportContext = {}) => {
  const exportFilters = exportContext?.filters;
  const appliedPayload = exportContext?.generalSettings?.additionalPayloadData || {};
  const exportDateRange = Array.isArray(exportFilters?.dateRange?.range)
    ? exportFilters.dateRange.range
    : [];
  const payloadDateRange = Array.isArray(appliedPayload?.dateRange?.range)
    ? appliedPayload.dateRange.range
    : [];
  const fallbackDateRange = [
    appliedPayload?.startDate || appliedPayload?.fromdate || null,
    appliedPayload?.endDate || appliedPayload?.todate || null,
  ];

  return getMonthLabelFromDateRange(
    exportDateRange.length > 0
      ? exportDateRange
      : payloadDateRange.length > 0
        ? payloadDateRange
        : fallbackDateRange,
  );
};

const getProjectNameFromExportFilters = (filters = {}) => {
  if (Array.isArray(filters)) {
    const projectNameFilter = filters.find((filterItem) => filterItem?.field === 'projectName');
    return getReadableSelectedLabel(projectNameFilter?.selectedValue)
      || getSelectedOptionLabel(projectNameFilter?.selectedValue, {
        allowIdentifierFallback: false,
      });
  }

  return getReadableSelectedLabel(filters?.projectName)
    || getSelectedOptionLabel(filters?.projectName, {
      allowIdentifierFallback: false,
    });
};

const buildActivityExportFileName = (exportContext = {}) => {
  const projectName = exportContext?.projectName
    || getProjectNameFromExportFilters(exportContext?.filters)
    || getProjectNameFromAppliedFilters(exportContext?.generalSettings?.additionalPayloadData)
    || 'Report';
  const monthLabel = getExportMonthLabel(exportContext);

  return `Utilization Activity Report - ${sanitizeExportFileSegment(projectName)} - ${
    sanitizeExportFileSegment(monthLabel)
  }`;
};

const buildActivityExportSheetName = (exportContext = {}) => {
  void exportContext;
  return UTILIZATION_ACTIVITY_REPORT_TITLE;
};

const getProjectNameFromReportTitle = (reportTitle = '') => {
  const titleParts = `${reportTitle}`.split(' - ');
  return titleParts.length > 1
    ? titleParts.slice(1).join(' - ')
      .trim()
    : '';
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton = !isFilterButton
      && !isResetButton
      && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('activity-report-reset-btn', isResetButton);
    buttonElement.classList.toggle('activity-report-export-btn', isDownloadButton);
  });
};

const DEFAULT_REPORT_TITLE = UTILIZATION_ACTIVITY_REPORT_TITLE;
const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  limit: 10,
  total: 0,
});
const DEFAULT_LOADER_SKELETON = Object.freeze({
  rows: DEFAULT_PAGINATION.limit,
  bodyCells: 2,
  columns: 5,
});
// TABLE_SCROLL_RATIO removed - using flex layout instead of fixed height
const SUMMARY_ROW_ID = 'summary-row';
const INDIAN_NUMBER_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const REPORT_DATE_HEADER_PATTERN
  = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{2}-[A-Za-z]{3}-\d{4})$/;
const HOLIDAY_LIST_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ACTIVITY_REPORT_DATE_PATTERN = /^\d{2}-[A-Za-z]{3}-\d{4}$/;

const baseURL = getActivityReportUrl();

const normalizeHolidayDate = (value = '') => {
  const trimmedValue = `${value ?? ''}`.trim();

  if (!trimmedValue) {
    return '';
  }

  if (HOLIDAY_LIST_DATE_PATTERN.test(trimmedValue)) {
    return trimmedValue;
  }

  const dateMatch = trimmedValue.match(ACTIVITY_REPORT_DATE_PATTERN);

  if (!dateMatch) {
    return '';
  }

  const [day, monthShort, year] = trimmedValue.split('-');
  const monthMap = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };

  return monthMap[monthShort]
    ? `${year}-${monthMap[monthShort]}-${day}`
    : '';
};

const getHolidayDateFromColumn = (column = {}) => (
  normalizeHolidayDate(column?.dateKey)
    || (() => {
      const headerParts = getColumnHeaderText(column).split(' ');
      return normalizeHolidayDate(headerParts[headerParts.length - 1]);
    })()
);

const decorateHolidayColumns = (tableConfiguration = [], holidayDates = new Set()) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => {
      const holidayDate = getHolidayDateFromColumn(column);
      const isHoliday = Boolean(column?.isHoliday)
        || (holidayDate ? holidayDates.has(holidayDate) : false);

      return {
        ...column,
        isHoliday,
      };
    })
    : []
);

const getColumnHeaderText = (column = {}) => {
  const headerCandidates = [
    column?.header,
    column?.label,
    column?.filterLabel,
    column?.menuLabel,
    column?.displayLabel,
    column?.field,
  ];

  const headerText = headerCandidates.find((candidate) => typeof candidate === 'string');

  return headerText ? headerText.trim() : '';
};

const isActivityDisplayColumn = (column = {}) => column?.field === 'activity';

const isTotalEffortDisplayColumn = (column = {}) => {
  const headerText = getColumnHeaderText(column).toLowerCase();

  return column?.field === 'total' || headerText.startsWith('total effort');
};

const isDateDisplayColumn = (column = {}) => REPORT_DATE_HEADER_PATTERN.test(
  getColumnHeaderText(column),
);

const getNumericDisplayFields = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration
      .filter((column) => isTotalEffortDisplayColumn(column) || isDateDisplayColumn(column))
      .map((column) => column?.field)
      .filter(Boolean)
    : []
);

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  const isNegativeValue = trimmedValue.startsWith('(') && trimmedValue.endsWith(')');
  const normalizedValue = trimmedValue.replace(/,/g, '').replace(/[()%]/g, '');
  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return isNegativeValue ? -parsedValue : parsedValue;
};

const formatIndianDecimal = (value) => INDIAN_NUMBER_FORMATTER.format(parseNumericValue(value));

const formatNumericFieldsInRow = (row = {}, numericFields = []) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const formattedRow = { ...row };

  numericFields.forEach((field) => {
    formattedRow[field] = formatIndianDecimal(row[field]);
  });

  if (Array.isArray(row.children)) {
    formattedRow.children = row.children.map((childRow) => formatNumericFieldsInRow(
      childRow,
      numericFields,
    ));
  }

  return formattedRow;
};

const formatTableData = (tableData = [], numericFields = []) => (
  Array.isArray(tableData)
    ? tableData.map((row) => formatNumericFieldsInRow(row, numericFields))
    : []
);

const renderStackedHeader = (
  primaryLabel,
  secondaryLabel,
  variant = '',
  tertiaryLabel = '',
) => (
  <span className={`activity-report-header-stack ${variant}`.trim()}>
    <span className="activity-report-header-primary">{primaryLabel}</span>
    <span className="activity-report-header-secondary">{secondaryLabel}</span>
    {tertiaryLabel ? (
      <span className="activity-report-header-tertiary">{tertiaryLabel}</span>
    ) : null}
  </span>
);

const buildDisplayHeader = (column = {}) => {
  const headerText = getColumnHeaderText(column);

  if (isTotalEffortDisplayColumn(column)) {
    return renderStackedHeader(
      'Total effort',
      '(hours)',
      'activity-report-header-stack--numeric',
    );
  }

  const dateHeaderMatch = headerText.match(REPORT_DATE_HEADER_PATTERN);

  if (dateHeaderMatch) {
    const [, weekDay, dayLabel] = dateHeaderMatch;

    if (column?.isHoliday) {
      return renderStackedHeader(
        'Holiday',
        weekDay,
        'activity-report-header-stack--numeric',
        dayLabel,
      );
    }

    return renderStackedHeader(
      weekDay,
      dayLabel,
      'activity-report-header-stack--numeric',
    );
  }

  return headerText;
};

const removeInlineSortIcon = (menuItems = [], shouldHideSortIcon = false) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => (
          item?.type === 'sort' && shouldHideSortIcon
            ? {
              ...item,
              showSortButtonInHeader: false,
            }
            : item
        ))
        : menuItem?.items,
    }))
    : []
);

const normalizeFilterSelectionValue = (selectedValue) => {
  if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
    return null;
  }

  if (typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return selectedValue.value
      ?? selectedValue.id
      ?? selectedValue.name
      ?? selectedValue.label
      ?? null;
  }

  return selectedValue;
};

const normalizeMultiSelectMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.template !== 'multiSelect' && item?.template !== 'choice') {
            return item;
          }

          const normalizedSelectedValues = Array.isArray(item?.selectedValues)
            ? item.selectedValues
              .map(normalizeFilterSelectionValue)
              .filter((selectedValue) => selectedValue !== null)
            : item?.selectedValues;

          return {
            ...item,
            selectedValues: normalizedSelectedValues,
          };
        })
        : menuItem?.items,
    }))
    : []
);

const applyActivityReportFilterMetadata = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.field === 'projectName') {
            return {
              ...item,
              isRequired: true,
              requiredOnMainApply: true,
              requiredMessage: 'Select Project Name is mandatory.',
              requiredValidationMessage: 'Select Project Name is mandatory.',
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                requiredOnMainApply: true,
                requiredValidationMessage: 'Select Project Name is mandatory.',
              },
            };
          }

          if (item?.field === 'dateRange') {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                maxRangeDays: 31,
                maxRangeValidationMessage: 'Date range has to be within 31 days.',
              },
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : []
);

const ensureMenuItems = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems) ? column.menuItems : [],
    }))
    : []
);

const transformTableConfiguration = (tableConfiguration = []) => ensureMenuItems(
  tableConfiguration,
).map((column) => {
  const headerText = getColumnHeaderText(column);
  const shouldShowHeaderActions
    = isActivityDisplayColumn(column) || isTotalEffortDisplayColumn(column);
  const shouldDefaultPinColumn = isActivityDisplayColumn(column);
  const shouldShowPinButton = false;
  const hideInlineSortIcon
    = shouldShowHeaderActions;

  return {
    ...column,
    filter: shouldShowHeaderActions ? true : column?.filter,
    pinnable: shouldShowHeaderActions ? false : column?.pinnable,
    defaultPinned: shouldShowHeaderActions
      ? shouldDefaultPinColumn
      : column?.defaultPinned,
    isPinned: shouldShowHeaderActions
      ? false
      : column?.isPinned,
    filterVisible: shouldShowHeaderActions
      ? false
      : column?.filterVisible,
    pinVisible: shouldShowHeaderActions
      ? shouldShowPinButton
      : column?.pinVisible,
    header: buildDisplayHeader({
      ...column,
      header: headerText,
    }),
    label: column?.label || headerText,
    filterLabel: column?.filterLabel || headerText,
    menuLabel: column?.menuLabel || headerText,
    displayLabel: column?.displayLabel || headerText,
    menuItems: removeInlineSortIcon(
      applyActivityReportFilterMetadata(
        normalizeMultiSelectMenuItems(column?.menuItems),
      ),
      hideInlineSortIcon,
    ),
  };
});

const FILTER_STATE_KEYS = [
  'selectedValues',
  'selectedValue',
  'selectedLabel',
  'inputValue',
  'range',
  'condition',
  'value',
];

const hydrateFilterState = (
  tableConfiguration = [],
  filterStateMap = new Map(),
) => {
  if (!Array.isArray(tableConfiguration) || !(filterStateMap instanceof Map) || filterStateMap.size === 0) {
    return tableConfiguration;
  }

  return tableConfiguration.map((column) => ({
    ...column,
    menuItems: Array.isArray(column?.menuItems)
      ? column.menuItems.map((menuItem) => ({
        ...menuItem,
        items: Array.isArray(menuItem?.items)
          ? menuItem.items.map((item) => mergeFilterState(filterStateMap.get(item?.field), item))
          : menuItem?.items,
      }))
      : column?.menuItems,
  }));
};

const mergeFilterState = (previousItem = null, nextItem = null) => {
  if (!previousItem || !nextItem || nextItem?.type !== 'filter') {
    return nextItem;
  }

  const mergedItem = { ...nextItem };

  FILTER_STATE_KEYS.forEach((key) => {
    if (previousItem[key] !== undefined) {
      mergedItem[key] = previousItem[key];
    }
  });

  return mergedItem;
};

const mergeMenuItemState = (previousColumn = null, nextColumn = null) => {
  if (!nextColumn || !Array.isArray(nextColumn.menuItems)) {
    return nextColumn;
  }

  const previousFilterItems = new Map(
    flattenMenuItems(previousColumn?.menuItems)
      .filter((item) => item?.field)
      .map((item) => [item.field, item]),
  );

  return {
    ...nextColumn,
    menuItems: nextColumn.menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => mergeFilterState(previousFilterItems.get(item?.field), item))
        : menuItem?.items,
    })),
  };
};

const mergeTableConfigurationState = (previousConfiguration = [], nextConfiguration = []) => {
  if (!Array.isArray(nextConfiguration) || nextConfiguration.length === 0) {
    return Array.isArray(previousConfiguration) ? previousConfiguration : [];
  }

  const previousColumnsByField = new Map(
    Array.isArray(previousConfiguration)
      ? previousConfiguration
        .filter((column) => column?.field)
        .map((column) => [column.field, column])
      : [],
  );

  return nextConfiguration.map((column) => mergeMenuItemState(
    previousColumnsByField.get(column?.field),
    column,
  ));
};

const buildSummaryRow = (summary = null) => {
  if (!summary || typeof summary !== 'object' || Object.keys(summary).length === 0) {
    return null;
  }

  return {
    ...summary,
    id: summary.id || SUMMARY_ROW_ID,
    rowConfiguration: {
      ...(summary.rowConfiguration || {}),
      isFrozen: true,
      backgroundColor: summary.rowConfiguration?.backgroundColor || '#eceff5',
    },
  };
};

const buildPaginationMeta = (response = {}, fallbackPagination = DEFAULT_PAGINATION) => {
  const pagination = response?.data?.pagination
    || response?.data?.paginationConfig
    || response?.data?.data?.pagination
    || response?.data?.data?.paginationConfig
    || {};

  const rawPage = Number(
    pagination?.page
      ?? pagination?.currentPage
      ?? response?.meta?.page
      ?? fallbackPagination?.page,
  );
  const rawLimit = Number(
    pagination?.limit
      ?? pagination?.pageSize
      ?? pagination?.rows
      ?? response?.meta?.limit
      ?? fallbackPagination?.limit,
  );
  const rawTotal = Number(
    pagination?.total
      ?? pagination?.count
      ?? pagination?.totalRecords
      ?? response?.meta?.total,
  );

  const tableDataLength = Array.isArray(response?.data?.tableData)
    ? response.data.tableData.length
    : Array.isArray(response?.data?.data?.tableData)
      ? response.data.data.tableData.length
      : 0;

  return {
    ...(response?.meta || {}),
    page: rawPage > 0 ? rawPage : (fallbackPagination?.page > 0 ? fallbackPagination.page : 1),
    limit: rawLimit > 0 ? rawLimit : (fallbackPagination?.limit > 0 ? fallbackPagination.limit : 10),
    total: Number.isFinite(rawTotal) && (rawTotal > 0 || tableDataLength === 0)
      ? rawTotal
      : tableDataLength,
  };
};

const syncPaginationFromResponse = (setPagination, response) => {
  setPagination((previousPagination) => {
    const meta = response?.meta && typeof response.meta === 'object'
      ? response.meta
      : buildPaginationMeta(response, previousPagination);

    return {
      ...previousPagination,
      page: Number(meta.page) > 0 ? Number(meta.page) : previousPagination.page,
      limit: Number(meta.limit) > 0 ? Number(meta.limit) : previousPagination.limit,
      total: Number(meta.total) >= 0 ? Number(meta.total) : previousPagination.total,
    };
  });
};

const applyActivityReportHeaderClass = (rootNode) => {
  if (!rootNode) return;

  const headers = rootNode.querySelectorAll('.project-overview-header');
  headers.forEach((headerElement) => {
    headerElement.classList.add('activity-report-header');
  });
};

const applyActivityTableViewport = (rootNode) => {
  if (!rootNode) return;

  const tableContainers = rootNode.querySelectorAll('.table-container');
  tableContainers.forEach((tableContainerElement) => {
    tableContainerElement.classList.add('activity-report-table-viewport');
  });
};

const applyBodyCellHoverTitles = (rootNode) => {
  if (!rootNode) return;

  const bodyCells = rootNode.querySelectorAll(
    '.activity-report-table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
      return;
    }

    td.removeAttribute('title');
    if (contentElement !== td) {
      contentElement.removeAttribute('title');
    }
  });
};

const hideActivityPagination = (rootNode) => {
  const scopeNode = rootNode || document;
  if (!scopeNode) return;

  const paginationControls = scopeNode.querySelectorAll(
    '.activity-report-table-utility .p-paginator-rpp-options,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-dropdown,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-select,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-dropdown-trigger,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-select-trigger,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-dropdown-label,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-select-label,'
      + ' .activity-report-table-utility .p-paginator-rpp-options .p-paginator-rpp-label,'
      + ' .activity-report-table-utility .p-paginator .p-dropdown,'
      + ' .activity-report-table-utility .p-paginator .p-select',
  );

  paginationControls.forEach((element) => {
    element.style.setProperty('display', 'none', 'important');
    element.style.setProperty('visibility', 'hidden', 'important');
    element.style.setProperty('width', '0', 'important');
    element.style.setProperty('min-width', '0', 'important');
    element.style.setProperty('padding', '0', 'important');
    element.style.setProperty('margin', '0', 'important');
    element.style.setProperty('overflow', 'hidden', 'important');
  });
};

const applyProjectHeadingLink = ({
  rootNode,
  reportTitle,
  projectName,
  isClickable,
}) => {
  if (!rootNode) {
    return;
  }

  const headingElements = rootNode.querySelectorAll(
    '.project-overview-header .toolbar-heading, .activity-report-header .toolbar-heading',
  );

  headingElements.forEach((headingElement) => {
    if (!isClickable || !projectName) {
      if (headingElement.textContent !== reportTitle) {
        headingElement.textContent = reportTitle;
      }
      headingElement.style.cursor = '';
      return;
    }

    if (headingElement.textContent === `${ACTIVITY_HEADING_PREFIX}${projectName}`
      && headingElement.querySelector(`.${ACTIVITY_HEADING_LINK_CLASS}`)) {
      headingElement.style.cursor = '';
      return;
    }

    headingElement.replaceChildren();

    const prefixNode = document.createTextNode(ACTIVITY_HEADING_PREFIX);
    const projectNameNode = document.createElement('span');
    projectNameNode.className = ACTIVITY_HEADING_LINK_CLASS;
    projectNameNode.textContent = projectName;
    projectNameNode.style.cursor = 'pointer';

    headingElement.append(prefixNode, projectNameNode);
    headingElement.style.cursor = '';
  });
};

const useActivityReportTableEffects = ({
  containerRef,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const updateTableClasses = () => {
      applyActivityTableViewport(rootNode);
      applyActivityReportHeaderClass(rootNode);
      applyBodyCellHoverTitles(rootNode);
      hideActivityPagination(rootNode);
      applyToolbarButtonClasses(rootNode);
    };

    const timer = setTimeout(updateTableClasses, 500);
    const observer = new MutationObserver(updateTableClasses);
    const tableContainer = rootNode?.querySelector('.activity-report-table-utility');

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [containerRef]);
};

const useProjectNameTitleSync = ({
  appliedFilterPayload,
  projectNameFilterUrl,
  accessToken,
  utilityUserInfo,
  setReportTitle,
}) => {
  useEffect(() => {
    const selectedProject = appliedFilterPayload?.projectName;
    const selectedProjectId = selectedProject?.id ?? selectedProject?.value ?? null;
    const selectedProjectName = `${selectedProject?.name || selectedProject?.label || ''}`.trim();

    if (!selectedProjectId || !projectNameFilterUrl || !accessToken) {
      return undefined;
    }

    if (selectedProjectName && selectedProjectName !== `${selectedProjectId}`.trim()) {
      setReportTitle(`${UTILIZATION_ACTIVITY_REPORT_TITLE} - ${selectedProjectName}`);
      return undefined;
    }

    let isMounted = true;

    const fetchProjectName = async () => {
      try {
        const response = await fetch(projectNameFilterUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: accessToken,
          },
          body: JSON.stringify({
            payload: {
              searchQuery: '',
              userInfo: utilityUserInfo,
              ...appliedFilterPayload,
            },
          }),
        });

        const responsePayload = await response.json();
        const projectOptions = Array.isArray(responsePayload?.data) ? responsePayload.data : [];
        const matchedProject = projectOptions.find((projectOption) => (
          [`${projectOption?.id ?? ''}`.trim(), `${projectOption?.value ?? ''}`.trim()]
            .includes(`${selectedProjectId}`.trim())
        ));
        const matchedProjectName = `${matchedProject?.name || matchedProject?.label || ''}`.trim();

        if (isMounted && matchedProjectName) {
          setReportTitle(`${UTILIZATION_ACTIVITY_REPORT_TITLE} - ${matchedProjectName}`);
        }
      } catch {
        // Keep the current title if the lookup endpoint fails.
      }
    };

    fetchProjectName();

    return () => {
      isMounted = false;
    };
  }, [
    accessToken,
    appliedFilterPayload,
    projectNameFilterUrl,
    setReportTitle,
    utilityUserInfo,
  ]);
};

const ActivityReportMoreActions = () => {
  const navigate = useNavigate();
  const moreMenuRef = useRef(null);

  const menuItems = useMemo(() => [
    {
      label: 'Utilization Employee Report',
      command: () => navigate(UTILIZATION_EMPLOYEE_REPORT_ROUTE),
    },
  ], [navigate]);

  return (
    <Fragment>
      <Menu
        ref={moreMenuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        className="activity-report-more-menu"
      />
      <button
        type="button"
        className="p-button p-component p-button-icon-only p-button-text activity-report-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        aria-label="More activity report links"
        title="More options"
      >
        <span className="activity-report-more-icon">
          <img src={moreIcon} alt="" />
        </span>
      </button>
    </Fragment>
  );
};

const ActivityReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const paginationRequestRef = useRef(DEFAULT_PAGINATION);
  const tableConfigurationRef = useRef([]);
  const lastResponseRef = useRef(null);
  const holidayDatesRef = useRef(new Set());
  const activeFilterStateMapRef = useRef(new Map());
  const preserveFiltersRef = useRef(false);

  // Removed tableScrollHeight state - using flex layout instead
  const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
  const [, setPagination] = useState(DEFAULT_PAGINATION);
  const [appliedFilterPayload, setAppliedFilterPayload] = useState({});
  const [projectNameFilterUrl, setProjectNameFilterUrl] = useState('');
  const [holidayMetadataVersion, setHolidayMetadataVersion] = useState(0);

  const utilityUserInfo = useMemo(() => ({
    id: userProfile?.employeeId,
    emailId: userProfile?.email,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    employeeId: userProfile?.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  const openProjectDetailsInNewTab = useCallback((projectId) => {
    if (!projectId) {
      return;
    }

    window.open(buildProjectDetailsUrl(projectId), '_blank', 'noopener,noreferrer');
  }, []);

  const selectedProjectValue = appliedFilterPayload?.projectName || null;
  const selectedProjectId = selectedProjectValue?.id || selectedProjectValue?.value || null;
  const selectedProjectName = getProjectNameFromReportTitle(reportTitle);

  useActivityReportTableEffects({
    containerRef,
  });
  useProjectNameTitleSync({
    appliedFilterPayload,
    projectNameFilterUrl,
    accessToken: userProfile?.access_token,
    utilityUserInfo,
    setReportTitle,
  });

  useEffect(() => {
    const rootNode = containerRef.current;
    if (!rootNode) {
      return undefined;
    }

    applyProjectHeadingLink({
      rootNode,
      reportTitle,
      projectName: selectedProjectName,
      isClickable: Boolean(selectedProjectName && selectedProjectId),
    });

    return undefined;
  }, [reportTitle, selectedProjectId, selectedProjectName]);

  useEffect(() => {
    const rootNode = containerRef.current;
    if (!rootNode) {
      return undefined;
    }

    const handleProjectHeadingClick = (event) => {
      const projectNameLink = event.target.closest(`.${ACTIVITY_HEADING_LINK_CLASS}`);
      if (!projectNameLink || !rootNode.contains(projectNameLink)) {
        return;
      }

      openProjectDetailsInNewTab(selectedProjectId);
    };

    rootNode.addEventListener('click', handleProjectHeadingClick);

    return () => {
      rootNode.removeEventListener('click', handleProjectHeadingClick);
    };
  }, [openProjectDetailsInNewTab, selectedProjectId]);

  useEffect(() => {
    const dateRange = Array.isArray(appliedFilterPayload?.dateRange?.range)
      ? appliedFilterPayload.dateRange.range
      : [];
    const startDate = dateRange[0];
    const endDate = dateRange[1];

    if (!startDate || !endDate) {
      holidayDatesRef.current = new Set();
      if (lastResponseRef.current) {
        lastResponseRef.current.data = {
          ...lastResponseRef.current.data,
          tableConfiguration: decorateHolidayColumns(
            lastResponseRef.current.data?.tableConfiguration,
            holidayDatesRef.current,
          ),
          data: {
            ...lastResponseRef.current.data?.data,
            tableConfiguration: decorateHolidayColumns(
              lastResponseRef.current.data?.data?.tableConfiguration,
              holidayDatesRef.current,
            ),
          },
        };
        setHolidayMetadataVersion((version) => version + 1);
      }
      return undefined;
    }

    let isActive = true;
    const abortController = new AbortController();

    const loadHolidayDates = async () => {
      try {
        const holidayRequest = getActivityReportHolidayListRequest({
          startDate,
          endDate,
          token: userProfile?.access_token,
        });

        const response = await fetch(holidayRequest.url, {
          method: holidayRequest.method,
          headers: holidayRequest.headers,
          signal: abortController.signal,
        });

        const responseBody = await response.json();
        const holidayRows = Array.isArray(responseBody?.holidayDetails)
          ? responseBody.holidayDetails
          : Array.isArray(responseBody?.data?.holidayDetails)
            ? responseBody.data.holidayDetails
            : Array.isArray(responseBody?.data)
              ? responseBody.data
              : [];
        const nextHolidayDates = new Set(
          holidayRows
            .map((holiday) => normalizeHolidayDate(
              holiday?.holidayDate || holiday?.holiday_date || holiday?.date,
            ))
            .filter(Boolean),
        );

        if (!isActive) {
          return;
        }

        holidayDatesRef.current = nextHolidayDates;

        if (lastResponseRef.current) {
          lastResponseRef.current.data = {
            ...lastResponseRef.current.data,
            tableConfiguration: decorateHolidayColumns(
              lastResponseRef.current.data?.tableConfiguration,
              holidayDatesRef.current,
            ),
            data: {
              ...lastResponseRef.current.data?.data,
              tableConfiguration: decorateHolidayColumns(
                lastResponseRef.current.data?.data?.tableConfiguration,
                holidayDatesRef.current,
              ),
            },
          };
          setHolidayMetadataVersion((version) => version + 1);
        }
      } catch {
        if (!isActive) {
          return;
        }
        holidayDatesRef.current = new Set();
      }
    };

    (async () => {
      await loadHolidayDates();
    })();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [appliedFilterPayload?.dateRange?.range, userProfile?.access_token]);

  const handleFilteredApiResponse = useCallback((response) => {
    lastResponseRef.current = response;
    const payload = getResponsePayload(response);
    const requestPayload = parseRequestPayload(response);
    const requestFilterStateMap = buildRequestFilterStateMap(requestPayload);
    const filterStateMap = requestFilterStateMap.size > 0
      ? requestFilterStateMap
      : activeFilterStateMapRef.current;
    const nextTableConfiguration = transformTableConfiguration(payload.tableConfiguration);
    const shouldPreserveFilterState = preserveFiltersRef.current;
    const tableConfigurationWithPreviousState = shouldPreserveFilterState
      ? mergeTableConfigurationState(tableConfigurationRef.current, nextTableConfiguration)
      : nextTableConfiguration;
    const tableConfiguration = hydrateFilterState(
      tableConfigurationWithPreviousState,
      filterStateMap,
    );
    const tableConfigurationWithHolidayFlags = decorateHolidayColumns(
      tableConfiguration,
      holidayDatesRef.current,
    );
    const numericDisplayFields = getNumericDisplayFields(tableConfiguration);
    const rawTableData = Array.isArray(payload.tableData) ? payload.tableData : [];
    const formattedTableData = formatTableData(rawTableData, numericDisplayFields);
    const formattedSummary = formatNumericFieldsInRow(payload.summary, numericDisplayFields);
    const summaryRow = formattedTableData.length > 0 ? buildSummaryRow(formattedSummary) : null;
    const tableData = summaryRow ? [...formattedTableData, summaryRow] : formattedTableData;
    const paginationMeta = buildPaginationMeta(response, paginationRequestRef.current);
    const tableConfigurationUpdate = tableConfigurationWithHolidayFlags.length
      ? { tableConfiguration: tableConfigurationWithHolidayFlags }
      : {};
    const {
      tableConfiguration: omittedResponseTableConfiguration,
      data: responseNestedData,
      ...responseData
    } = response?.data || {};
    const {
      tableConfiguration: omittedPayloadTableConfiguration,
      ...payloadWithoutTableConfiguration
    } = payload || {};
    const {
      tableConfiguration: omittedNestedTableConfiguration,
      ...nestedDataRest
    } = responseNestedData || {};

    void omittedResponseTableConfiguration;
    void omittedPayloadTableConfiguration;
    void omittedNestedTableConfiguration;

    tableConfigurationRef.current = tableConfigurationWithHolidayFlags;
    if (requestFilterStateMap.size > 0) {
      activeFilterStateMapRef.current = cloneFilterStateMap(requestFilterStateMap);
    }
    preserveFiltersRef.current = false;

    response.meta = paginationMeta;

    response.data = {
      ...responseData,
      meta: {
        ...(response?.data?.meta || {}),
        ...paginationMeta,
      },
      pagination: paginationMeta,
      paginationConfig: paginationMeta,
      ...tableConfigurationUpdate,
      tableData,
      data: {
        ...nestedDataRest,
        ...payloadWithoutTableConfiguration,
        meta: {
          ...(payload?.meta || {}),
          ...paginationMeta,
        },
        pagination: paginationMeta,
        paginationConfig: paginationMeta,
        ...tableConfigurationUpdate,
        tableData,
      },
    };

    const nextAppliedFilterPayload = getAppliedFilterPayload(tableConfiguration);
    const projectNameFilterItem = getFilterMenuItem(tableConfiguration, 'projectName');
    const nextReportTitle = getReportTitleFromConfiguration(response, tableConfiguration);
    const nextProjectNameFilterUrl = projectNameFilterItem?.url || '';

    setReportTitle((previousTitle) => (
      previousTitle === nextReportTitle ? previousTitle : nextReportTitle
    ));
    setAppliedFilterPayload((previousPayload) => (
      areEqualByValue(previousPayload, nextAppliedFilterPayload)
        ? previousPayload
        : nextAppliedFilterPayload
    ));
    setProjectNameFilterUrl((previousUrl) => (
      previousUrl === nextProjectNameFilterUrl ? previousUrl : nextProjectNameFilterUrl
    ));
    syncPaginationFromResponse(setPagination, response);
  }, []);

  const handlePaginationChange = useCallback((nextPagination) => {
    preserveFiltersRef.current = true;
    setPagination((previousPagination) => {
      const nextLimit = Number(nextPagination?.limit) > 0
        ? Number(nextPagination.limit)
        : previousPagination.limit;
      const nextPage = Number(nextPagination?.page) > 0
        ? Number(nextPagination.page)
        : 1;
      const nextTotal = Number(nextPagination?.total) >= 0
        ? Number(nextPagination.total)
        : previousPagination.total;
      const normalizedPage = nextLimit !== previousPagination.limit ? 1 : nextPage;
      const normalizedPagination = {
        ...previousPagination,
        ...nextPagination,
        page: normalizedPage,
        limit: nextLimit,
        total: nextTotal,
      };

      paginationRequestRef.current = normalizedPagination;

      if (
        previousPagination.page === normalizedPagination.page
        && previousPagination.limit === normalizedPagination.limit
        && previousPagination.total === normalizedPagination.total
      ) {
        return previousPagination;
      }

      return normalizedPagination;
    });
  }, []);

  const tablePaginationConfig = useMemo(() => ({
    ...DEFAULT_PAGINATION,
    onPaginationChange: handlePaginationChange,
    rowsPerPageOptions: [],
  }), [handlePaginationChange]);

  const toolbarAdditionalElements = useMemo(() => [
    <ActivityReportMoreActions key="activity-report-more-actions" />,
  ], []);

  const buildSelectedProjectExportFileName = useCallback((exportContext = {}) => {
    const projectNameForExport = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters);

    return buildActivityExportFileName({
      ...exportContext,
      projectName: projectNameForExport,
      generalSettings: {
        ...exportContext?.generalSettings,
        additionalPayloadData: appliedFilterPayload,
      },
    });
  }, [appliedFilterPayload, reportTitle]);

  const buildSelectedProjectExportSheetName = useCallback((exportContext = {}) => {
    const projectNameForExport = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters)
      || 'Report';

    return buildActivityExportSheetName({
      ...exportContext,
      projectName: projectNameForExport,
      generalSettings: {
        ...exportContext?.generalSettings,
        additionalPayloadData: appliedFilterPayload,
      },
    });
  }, [appliedFilterPayload, reportTitle]);

  const buildExportReportTitle = useCallback((exportContext = {}) => {
    const projectNameForExport = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters);
    const monthLabel = getExportMonthLabel({
      ...exportContext,
      generalSettings: {
        ...exportContext?.generalSettings,
        additionalPayloadData: appliedFilterPayload,
      },
    });

    return projectNameForExport
      ? `${UTILIZATION_ACTIVITY_REPORT_TITLE} - ${projectNameForExport} - ${monthLabel}`
      : UTILIZATION_ACTIVITY_REPORT_TITLE;
  }, [appliedFilterPayload, reportTitle]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: utilityUserInfo,
      apiEndpoints: {
        getAllViews: {
          endpoint: 'views',
          enabled: false,
        },
        getFilteredData: {
          endpoint: '',
          enabled: true,
        },
        saveOrUpdateView: {
          endpoint: 'view',
          enabled: false,
        },
        deleteView: {
          endpoint: 'view-delete',
          enabled: false,
        },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: buildSelectedProjectExportFileName,
          sheetName: buildSelectedProjectExportSheetName,
          reportTitle: buildExportReportTitle,
        },
      },
      handler: {
        handleFilteredApiResponse,
      },
      additionalPayloadData: {},
      headers: {
        token: userProfile?.access_token,
      },
      showToolBar: true,
      showPagination: false,
      toolBarSettings: {
        showFilterOption: true,
        showAddNew: false,
        showDownloadExcel: true,
        downloadExcelFileName: buildSelectedProjectExportFileName,
        downloadExcelSheetName: buildSelectedProjectExportSheetName,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: reportTitle,
        additionalElements: toolbarAdditionalElements,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: DEFAULT_PAGINATION.limit,
      loaderSkeleton: DEFAULT_LOADER_SKELETON,
      paginationConfig: tablePaginationConfig,
      holidayMetadataVersion,
      showExpandCollapse: false,
      enableTreeTable: false,
      tableScrollHeight: 'flex',
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  }), [
    buildExportReportTitle,
    buildSelectedProjectExportFileName,
    buildSelectedProjectExportSheetName,
    reportTitle,
    tablePaginationConfig,
    holidayMetadataVersion,
    toolbarAdditionalElements,
    userProfile,
    utilityUserInfo,
  ]);

  if (!userProfile?.access_token) {
    return null;
  }

  return (
    <div className="activity-report-outer-layer" ref={containerRef}>
      <div className="activity-report-table-container">
        <div className="activity-report-table-utility">
          <TableUtility
            configuration={configuration}
          />
        </div>
      </div>
    </div>
  );
};

export default ActivityReport;
